import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';

/**
 * Modal shown when a user attempts to navigate away from a page with unsaved changes.
 * Offers three actions: Save Changes, Discard, Cancel.
 */
const UnsavedChangesModal = ({ show, onSave, onDiscard, onCancel }) => {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave();
    } catch (err) {
      console.error('Failed to save changes:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="bg-white rounded-4 shadow p-4"
            style={{ maxWidth: '440px', width: '100%' }}
          >
            <div className="d-flex align-items-center gap-2 mb-3">
              <AlertTriangle size={22} className="text-warning" />
              <h5 className="fw-bold mb-0">Unsaved Changes</h5>
            </div>

            <p className="text-muted mb-4" style={{ fontSize: '0.95rem' }}>
              You have unsaved changes that will be lost if you leave this page.
              Would you like to save your changes before navigating away?
            </p>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="outline-secondary"
                onClick={onCancel}
                disabled={isSaving}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                variant="outline-danger"
                onClick={onDiscard}
                disabled={isSaving}
                size="sm"
              >
                Discard
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={isSaving}
                size="sm"
              >
                {isSaving ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Saving…
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UnsavedChangesModal;
